import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useCenterInstructorMappings,
  useMapInstructor,
  useUnmapInstructor,
  useRegisterInstructorAtCenter,
  useDeregisterInstructor,
  useUpdateInstructorEmail,
  useDeleteInstructor,
  useMapAdminAsInstructor,
} from "@/hooks/useStudiomateIntegration";
import type {
  InstructorCandidate,
  CenterInstructor,
} from "@vlab-corporation/vlab-pilates-service-interface/instructor-mapping";

export function InstructorMappingPage() {
  const { centerId } = useParams<{ centerId: string }>();
  const { data, isLoading, isError, refetch } = useCenterInstructorMappings(Number(centerId));
  const mapMutation = useMapInstructor();
  const unmapMutation = useUnmapInstructor();
  const deregisterMutation = useDeregisterInstructor();
  const registerMutation = useRegisterInstructorAtCenter();
  const updateEmailMutation = useUpdateInstructorEmail();
  const deleteMutation = useDeleteInstructor();
  const mapAdminMutation = useMapAdminAsInstructor();

  const [selectedCandidate, setSelectedCandidate] = useState<InstructorCandidate | null>(null);
  const [ownerSelectedCandidate, setOwnerSelectedCandidate] = useState<InstructorCandidate | null>(
    null,
  );
  const [registerTarget, setRegisterTarget] = useState<InstructorCandidate | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [editingEmailFor, setEditingEmailFor] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<CenterInstructor | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isError || !data || data.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-red-700">
          {data?.error?.code === "NOT_CONNECTED" ? "외부 연동 필요" : "오류 발생"}
        </h2>
        <p className="text-red-600 mt-1">
          {data?.error?.message || "데이터를 불러올 수 없습니다."}
        </p>
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            다시 시도
          </button>
          <Link
            to={centerId ? `/centers/${centerId}` : "/centers"}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            센터 상세로 이동
          </Link>
        </div>
      </div>
    );
  }

  const { candidates, instructors, owner } = data;
  const isAdminAlreadyMapped = !!owner?.mapping;

  const handleMapAdmin = async (candidate: InstructorCandidate) => {
    const result = await mapAdminMutation.mutateAsync({
      studiomateStaffId: candidate.staffId,
      staffName: candidate.name,
      centerId: Number(centerId),
    });
    if (result.success) {
      refetch();
    }
  };

  const handleMap = async (instructor: CenterInstructor, candidate: InstructorCandidate) => {
    await mapMutation.mutateAsync({
      instructorId: instructor.instructorId,
      studiomateStaffId: candidate.staffId,
      staffName: candidate.name,
    });
    setSelectedCandidate(null);
    refetch();
  };

  // Firebase 강사 목록의 "해제"는 등록취소(register의 역연산)다. instructor 문서를
  // 제거하고 강사를 다시 미매핑 후보로 복귀시킨다. (owner 섹션의 해제는 unmapInstructor로
  // 매핑만 푼다 — 아래 원장 섹션 참고.)
  const handleDeregister = async (instructor: CenterInstructor) => {
    await deregisterMutation.mutateAsync({
      instructorId: instructor.instructorId,
      centerId: Number(centerId),
    });
    refetch();
  };

  const handleRegister = async (candidate: InstructorCandidate) => {
    setRegisterError(null);
    try {
      const result = await registerMutation.mutateAsync({
        staffId: candidate.staffId,
        centerId: Number(centerId),
      });
      if (result.success) {
        setRegisterTarget(null);
        refetch();
      } else {
        setRegisterError(result.error?.message ?? "등록에 실패했습니다.");
      }
    } catch (error) {
      // callable이 HttpsError를 throw하는 경로(internal 등) — 다이얼로그를 유지한 채 안내
      setRegisterError(
        error instanceof Error ? error.message : "등록 중 알 수 없는 오류가 발생했습니다.",
      );
    }
  };

  const handleUpdateEmail = async (instructorId: string) => {
    setEmailError("");
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailInput.trim()) {
      setEmailError("이메일을 입력해주세요.");
      return;
    }
    if (!EMAIL_REGEX.test(emailInput.trim())) {
      setEmailError("올바른 이메일 형식을 입력해주세요.");
      return;
    }

    const result = await updateEmailMutation.mutateAsync({
      instructorId,
      newEmail: emailInput.trim(),
    });

    if (result.success) {
      setEditingEmailFor(null);
      setEmailInput("");
      refetch();
    } else {
      setEmailError(result.error?.message || "이메일 변경에 실패했습니다.");
    }
  };

  const handleDelete = async (instructor: CenterInstructor) => {
    const result = await deleteMutation.mutateAsync({
      instructorId: instructor.instructorId,
    });
    if (result.success) {
      setDeleteTarget(null);
      refetch();
    }
  };

  const isStudiomateLocalEmail = (email?: string) => email?.endsWith("@studiomate.local");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to={centerId ? `/centers/${centerId}` : "/centers"}
              className="text-gray-500 hover:text-gray-700"
            >
              ← 센터 상세
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">강사 관리</h1>
          <p className="text-gray-600">
            강사 계정을 매핑하고, 매핑된 강사의 일지 작성 현황을 확인합니다.
          </p>
        </div>
      </div>

      {/* 원장(Owner) 매핑 */}
      {owner && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">원장</h2>
          </div>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">{owner.name}</div>
              </div>
              <div className="flex items-center gap-4">
                {owner.mapping ? (
                  <>
                    <div className="text-right">
                      <div className="text-sm text-green-600 font-medium">매핑됨</div>
                      <div className="text-sm text-gray-500">{owner.mapping.staffName}</div>
                    </div>
                    <button
                      onClick={async () => {
                        await unmapMutation.mutateAsync({
                          instructorId: owner.uid,
                        });
                        refetch();
                      }}
                      disabled={unmapMutation.isPending}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      해제
                    </button>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-gray-400">미매핑</div>
                    <select
                      className="px-3 py-1 border border-gray-300 rounded text-sm"
                      value=""
                      onChange={(e) => {
                        const c = candidates.find((x) => x.staffId === Number(e.target.value));
                        if (c) setOwnerSelectedCandidate(c);
                      }}
                    >
                      <option value="">강사 후보 선택</option>
                      {candidates.map((candidate) => (
                        <option key={candidate.staffId} value={candidate.staffId}>
                          {candidate.name}
                        </option>
                      ))}
                    </select>
                    {ownerSelectedCandidate ? (
                      <button
                        onClick={async () => {
                          await handleMapAdmin(ownerSelectedCandidate);
                          setOwnerSelectedCandidate(null);
                        }}
                        disabled={mapAdminMutation.isPending}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        {mapAdminMutation.isPending ? "매핑 중..." : "관리자 매핑"}
                      </button>
                    ) : (
                      <button
                        disabled
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded opacity-50 cursor-not-allowed"
                      >
                        관리자 매핑
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Firebase 강사 목록 */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Firebase 강사 ({instructors.length}명)
          </h2>
        </div>
        <div className="divide-y">
          {instructors.map((instructor) => (
            <div key={instructor.instructorId} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{instructor.name}</div>
                  <div className="text-sm text-gray-500">{instructor.contact || "연락처 없음"}</div>
                  {/* 이메일 상태 표시 */}
                  {instructor.email && !isStudiomateLocalEmail(instructor.email) ? (
                    <div className="text-sm text-blue-600">{instructor.email}</div>
                  ) : (
                    <div className="text-sm text-orange-500">이메일 미설정</div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {instructor.mapping ? (
                    <>
                      <div className="text-right">
                        <div className="text-sm text-green-600 font-medium">매핑됨</div>
                        <div className="text-sm text-gray-500">{instructor.mapping.staffName}</div>
                      </div>
                      <Link
                        to={`/centers/${centerId}/instructors/${instructor.instructorId}/journals`}
                        className="px-3 py-1 text-sm border border-blue-300 text-blue-600 rounded hover:bg-blue-50"
                      >
                        일지 현황 →
                      </Link>
                      {(!instructor.email || isStudiomateLocalEmail(instructor.email)) && (
                        <button
                          onClick={() => {
                            setEditingEmailFor(instructor.instructorId);
                            setEmailInput("");
                            setEmailError("");
                          }}
                          className="px-3 py-1 text-sm border border-orange-400 text-orange-600 rounded hover:bg-orange-50"
                        >
                          이메일 설정
                        </button>
                      )}
                      <button
                        onClick={() => handleDeregister(instructor)}
                        disabled={deregisterMutation.isPending}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        해제
                      </button>
                    </>
                  ) : (
                    <>
                      <select
                        className="px-3 py-1 border border-gray-300 rounded text-sm"
                        value=""
                        onChange={(e) => {
                          const c = candidates.find((x) => x.staffId === Number(e.target.value));
                          if (c) setSelectedCandidate(c);
                        }}
                      >
                        <option value="">강사 후보 선택</option>
                        {candidates.map((candidate) => (
                          <option key={candidate.staffId} value={candidate.staffId}>
                            {candidate.name}
                          </option>
                        ))}
                      </select>
                      {selectedCandidate && (
                        <button
                          onClick={() => handleMap(instructor, selectedCandidate)}
                          disabled={mapMutation.isPending}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          매핑
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => setDeleteTarget(instructor)}
                    className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50"
                  >
                    삭제
                  </button>
                </div>
              </div>

              {/* 인라인 이메일 편집 */}
              {editingEmailFor === instructor.instructorId && (
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="example@email.com"
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={() => handleUpdateEmail(instructor.instructorId)}
                    disabled={updateEmailMutation.isPending}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updateEmailMutation.isPending ? "저장 중..." : "저장"}
                  </button>
                  <button
                    onClick={() => {
                      setEditingEmailFor(null);
                      setEmailError("");
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                  >
                    취소
                  </button>
                  {emailError && <span className="text-sm text-red-500">{emailError}</span>}
                </div>
              )}
            </div>
          ))}
          {instructors.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">등록된 강사가 없습니다.</div>
          )}
        </div>
      </div>

      {/* 미매핑 강사 후보 */}
      {candidates.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              미매핑 강사 후보 ({candidates.length}명)
            </h2>
            <p className="text-sm text-gray-500">
              가입은 했지만 아직 이 센터의 강사로 등록되지 않은 계정입니다. &apos;강사로
              등록&apos;을 누르면 강사 목록에 추가됩니다.
            </p>
          </div>
          <div className="divide-y">
            {candidates.map((candidate) => (
              <div key={candidate.staffId} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">{candidate.name}</div>
                  <div className="text-sm text-gray-500">{candidate.mobile || "연락처 없음"}</div>
                </div>
                <div className="flex gap-2">
                  {!isAdminAlreadyMapped && (
                    <button
                      onClick={() => handleMapAdmin(candidate)}
                      disabled={mapAdminMutation.isPending}
                      className="px-3 py-1 text-sm border border-green-600 text-green-600 rounded hover:bg-green-50 disabled:opacity-50"
                    >
                      {mapAdminMutation.isPending ? "매핑 중..." : "관리자로 매핑"}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setRegisterTarget(candidate);
                      setRegisterError(null);
                    }}
                    className="px-3 py-1 text-sm border border-blue-600 text-blue-600 rounded hover:bg-blue-50"
                  >
                    강사로 등록
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 강사 등록 확인 다이얼로그 */}
      {registerTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">강사 등록</h3>
            <p className="mt-2 text-sm text-gray-600">
              &apos;{registerTarget.name}&apos;
              {registerTarget.mobile ? ` (${registerTarget.mobile})` : ""}
              을(를) 이 센터의 강사로 등록하시겠어요?
            </p>
            <p className="mt-1 text-sm text-gray-500">
              가입된 계정을 그대로 사용하며, 강사 목록에 추가하고 외부 강사 번호와 매핑합니다.
            </p>
            {registerError && <p className="mt-2 text-sm text-red-500">{registerError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setRegisterTarget(null);
                  setRegisterError(null);
                }}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => handleRegister(registerTarget)}
                disabled={registerMutation.isPending}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {registerMutation.isPending ? "등록 중..." : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 강사 삭제 확인 다이얼로그 */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">강사 삭제</h3>
            <p className="mt-2 text-sm text-gray-600">
              강사 &apos;{deleteTarget.name}&apos;을(를) 삭제하시겠습니까?
            </p>
            <p className="mt-1 text-sm text-red-600">
              계정, 매핑 정보가 모두 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
            </p>
            {deleteMutation.data && !deleteMutation.data.success && (
              <p className="mt-2 text-sm text-red-500">
                {deleteMutation.data.error?.message || "삭제에 실패했습니다."}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() => handleDelete(deleteTarget)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "삭제 중..." : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
